/**
 * Drop-in replacement for `import { toast } from 'sonner'`.
 *
 * Keeps the whole `sonner` package out of every static chunk (including the
 * entry): the module is dynamically imported only when a toast actually fires.
 * The first call also activates the lazy <Toaster> in __root (via toast-signal)
 * and queues the call until the Toaster has mounted, so no toast is lost.
 *
 * Note: unlike worldcoo's variant — which statically imports sonner because its
 * entry never touches toast — the import here is *dynamic*. This wrapper is
 * reached from the client entry (main.tsx's tRPC error link), so a static
 * sonner import would pull the whole package back into the entry chunk.
 */
import { activateToaster, enqueueToast } from './toast-signal'

type ToastApi = (typeof import('sonner'))['toast']

function defer(invoke: (toast: ToastApi) => void): void {
  activateToaster()
  enqueueToast(() => {
    void import('sonner').then(({ toast }) => invoke(toast))
  })
}

const noop = (() => {}) as unknown as ToastApi

export const toast: ToastApi = new Proxy(noop, {
  apply(_target, _thisArg, args: unknown[]) {
    defer((t) => (t as (...a: unknown[]) => unknown)(...args))
  },
  get(_target, prop) {
    return (...args: unknown[]) =>
      defer((t) => {
        const fn = (t as unknown as Record<PropertyKey, ((...a: unknown[]) => unknown) | undefined>)[prop]
        fn?.(...args)
      })
  },
})
